import { Dispatch, Ref, SetStateAction, memo, useCallback } from "react";
import { usePartialSubmitForOverview } from "src/apps/AppFlow/pages/hooks";
import { FlowLayoutItemsOverview } from "src/components";
import { ItemControl } from "src/hooks/useSubpages";
import { MortgageInformation as MortgageInfo } from "src/types/Applicant";

import { formatters } from "@fraction/shared";

const { DEBT_TYPE_MAP } = formatters.enums;

interface MortgageInformationProps {
  value?: ItemControl<MortgageInfo>;
  onChange: Dispatch<SetStateAction<ItemControl<MortgageInfo> | undefined>>;
  propertyAddress?: string;
  onNext: () => void;
  onBack: () => void;
  focusRef?: Ref<HTMLDivElement>;
  newItemTemplate?: Partial<MortgageInfo>;
  simple?: boolean;
  isSubpage?: boolean;
}

const MortgageInformation = ({
  value,
  onChange,
  propertyAddress,
  onNext,
  onBack,
  focusRef,
  newItemTemplate = {},
  simple,
  isSubpage = false,
  ...props
}: MortgageInformationProps) => {
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmitForOverview({
    value,
    onNext,
    shouldSubmit: !isSubpage,
  });

  const headerFormatter = useCallback(
    (val: MortgageInfo) => val?.mortgageBalanceAndLender?.lenderName || "Unknown",
    []
  );

  const subheaderFormatter = useCallback(
    (val: MortgageInfo) =>
      val?.mortgageRateAndType?.mortgageType ? DEBT_TYPE_MAP[val.mortgageRateAndType.mortgageType] : "",
    []
  );

  const descriptionFormatter = useCallback(
    (val: MortgageInfo) =>
      formatters.number.getCurrencyFromNumber(val?.mortgageBalanceAndLender?.amount, {
        inputInDollars: true,
      }),
    []
  );

  return (
    <FlowLayoutItemsOverview
      {...props}
      secondary
      simple={simple}
      focusRef={focusRef}
      newItemTemplate={newItemTemplate}
      itemControl={value}
      exampleHeader="Mortgage lender"
      exampleSubheader="Example"
      exampleDescription="$350,000.00"
      addItemButtonText="Add mortgage or HELOC"
      headerFormatter={headerFormatter}
      subheaderFormatter={subheaderFormatter}
      descriptionFormatter={descriptionFormatter}
      onNext={handleNext}
      onBack={onBack}
      onChange={onChange}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
      title="Any mortgages on the property?"
      description={`Please provide more details on any mortgages or other loans that you have on ${
        propertyAddress || "your property"
      }.`}
      skipButtonText="I have no other mortgages"
    />
  );
};

export default memo(MortgageInformation);
